import { addDays, format, startOfWeek } from 'date-fns'
import type {
  CoachComment,
  Difficulty,
  HomeworkTask,
  Sprint,
  TaskMeeting,
  TaskType,
} from './types'

/** Start of the current real week, Monday-anchored. */
export function currentWeekStart(): Date {
  return startOfWeek(new Date(), { weekStartsOn: 1 })
}

export function toKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

const CURRENT_SPRINT_NUMBER = 14

interface TaskTemplate {
  day: number // 0 = Monday ... 6 = Sunday
  type: TaskType
  title: string
  description: string
  difficulty?: Difficulty
  links: { label: string; url: string }[]
  meeting?: TaskMeeting
}

const WEEK_THEMES = [
  'Arrays & Hashing',
  'Two Pointers & Sliding Window',
  'Trees & Graphs',
  'Dynamic Programming I',
  'System Design Foundations',
  'Heaps & Intervals',
]

const WEEK_PLANS: TaskTemplate[][] = [
  // Week 0 (offset -4): Arrays & Hashing
  [
    {
      day: 0,
      type: 'leetcode',
      title: 'Two Sum',
      difficulty: 'Easy',
      description:
        'Classic hash map warm-up. Aim for O(n) time using a single pass with a complement lookup. Pay attention to how you articulate the trade-off between the brute-force and hash map approaches out loud.',
      links: [
        { label: 'LeetCode #1', url: 'https://leetcode.com/problems/two-sum/' },
      ],
    },
    {
      day: 0,
      type: 'video',
      title: 'Big-O Notation in 40 Minutes',
      description:
        'A refresher on asymptotic analysis. Focus on the sections about amortized complexity and space/time trade-offs — these come up constantly in follow-up questions.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=big+o+notation',
        },
      ],
    },
    {
      day: 1,
      type: 'leetcode',
      title: 'Group Anagrams',
      difficulty: 'Medium',
      description:
        'Practice choosing a canonical key. Compare sorting each string vs. character-count tuples as the hash key, and be ready to discuss which is better for long strings.',
      links: [
        {
          label: 'LeetCode #49',
          url: 'https://leetcode.com/problems/group-anagrams/',
        },
      ],
    },
    {
      day: 1,
      type: 'article',
      title: 'Hash Tables Under the Hood',
      description:
        'Read about collision resolution strategies: chaining vs. open addressing. Interviewers love asking what happens when a hash table degrades.',
      links: [
        {
          label: 'Read article',
          url: 'https://en.wikipedia.org/wiki/Hash_table',
        },
      ],
    },
    {
      day: 2,
      type: 'system-design',
      title: 'Design a URL Shortener',
      description:
        'First full design question. Work through requirements, capacity estimation, API design, data model, and the hash-generation strategy. Sketch the read-heavy vs. write-heavy paths separately.',
      links: [
        {
          label: 'Prompt & rubric',
          url: 'https://github.com/donnemartin/system-design-primer',
        },
      ],
    },
    {
      day: 3,
      type: 'leetcode',
      title: 'Top K Frequent Elements',
      difficulty: 'Medium',
      description:
        'Solve it twice: once with a heap, once with bucket sort. Be ready to explain when each wins.',
      links: [
        {
          label: 'LeetCode #347',
          url: 'https://leetcode.com/problems/top-k-frequent-elements/',
        },
      ],
    },
    {
      day: 3,
      type: 'exercise',
      title: 'Implement an LRU Cache from Scratch',
      description:
        'On-platform coding exercise. Build the doubly-linked-list + hash map combo without looking anything up. Target: all test cases green in under 35 minutes.',
      links: [],
    },
    {
      day: 4,
      type: 'video',
      title: 'How to Talk Through a Problem',
      description:
        'Communication drills. Note the framework: restate, clarify, plan, code, test. You will use this structure in every mock interview going forward.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=coding+interview+communication',
        },
      ],
    },
    {
      day: 5,
      type: 'leetcode',
      title: 'Longest Consecutive Sequence',
      difficulty: 'Medium',
      description:
        'The O(n) insight is subtle — only start counting from sequence beginnings. Great one to practice narrating your "aha" moment.',
      links: [
        {
          label: 'LeetCode #128',
          url: 'https://leetcode.com/problems/longest-consecutive-sequence/',
        },
      ],
    },
  ],
  // Week 1 (offset -3): Two Pointers & Sliding Window
  [
    {
      day: 0,
      type: 'leetcode',
      title: 'Valid Palindrome',
      difficulty: 'Easy',
      description:
        'Warm-up for the two-pointer pattern. Handle the character-filtering cleanly — messy index arithmetic is a common source of bugs under pressure.',
      links: [
        {
          label: 'LeetCode #125',
          url: 'https://leetcode.com/problems/valid-palindrome/',
        },
      ],
    },
    {
      day: 0,
      type: 'article',
      title: 'The Two-Pointer Pattern, Explained',
      description:
        'Covers converging pointers, fast/slow pointers, and partition pointers. Make a note of the invariant each variant maintains.',
      links: [
        {
          label: 'Read article',
          url: 'https://leetcode.com/discuss/study-guide',
        },
      ],
    },
    {
      day: 1,
      type: 'leetcode',
      title: '3Sum',
      difficulty: 'Medium',
      description:
        'Sort + two pointers with careful duplicate skipping. This is a top-5 most-asked question — drill it until the dedupe logic is second nature.',
      links: [
        { label: 'LeetCode #15', url: 'https://leetcode.com/problems/3sum/' },
      ],
    },
    {
      day: 2,
      type: 'system-design',
      title: 'Design a Rate Limiter',
      description:
        'Compare token bucket, leaky bucket, fixed window, and sliding window log. Discuss where the limiter lives (client, gateway, service) and the distributed-state problem.',
      links: [
        {
          label: 'Prompt & rubric',
          url: 'https://github.com/donnemartin/system-design-primer',
        },
      ],
    },
    {
      day: 3,
      type: 'leetcode',
      title: 'Longest Substring Without Repeating Characters',
      difficulty: 'Medium',
      description:
        'Canonical sliding window. Focus on when to shrink the window and what state you maintain — practice explaining the window invariant.',
      links: [
        {
          label: 'LeetCode #3',
          url: 'https://leetcode.com/problems/longest-substring-without-repeating-characters/',
        },
      ],
    },
    {
      day: 3,
      type: 'video',
      title: 'Sliding Window Masterclass',
      description:
        'Walks through fixed vs. variable window templates. Pause at each example and predict the next line before it is revealed.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=sliding+window+technique',
        },
      ],
    },
    {
      day: 4,
      type: 'exercise',
      title: 'Sliding Window Drills (5 problems)',
      description:
        'Timed on-platform drill set: five short sliding-window problems, 12 minutes each. Goal is pattern recognition speed, not perfection.',
      links: [],
    },
    {
      day: 5,
      type: 'leetcode',
      title: 'Minimum Window Substring',
      difficulty: 'Hard',
      description:
        'The hardest window problem in the set. Get the need/have counting technique down — if you can do this one cold, every other window problem feels easy.',
      links: [
        {
          label: 'LeetCode #76',
          url: 'https://leetcode.com/problems/minimum-window-substring/',
        },
      ],
    },
  ],
  // Week 2 (offset -2): Trees & Graphs
  [
    {
      day: 0,
      type: 'leetcode',
      title: 'Invert Binary Tree',
      difficulty: 'Easy',
      description:
        'Yes, the famous one. Do it recursively and iteratively. Practice writing clean recursive code with clear base cases.',
      links: [
        {
          label: 'LeetCode #226',
          url: 'https://leetcode.com/problems/invert-binary-tree/',
        },
      ],
    },
    {
      day: 0,
      type: 'video',
      title: 'DFS vs BFS: When to Use Which',
      description:
        'Builds the decision framework: shortest path or level-order → BFS; exhaustive search, backtracking, or path problems → DFS.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=dfs+vs+bfs',
        },
      ],
    },
    {
      day: 1,
      type: 'leetcode',
      title: 'Binary Tree Level Order Traversal',
      difficulty: 'Medium',
      description:
        'BFS with a queue, tracking level boundaries. This template extends to at least a dozen other tree questions.',
      links: [
        {
          label: 'LeetCode #102',
          url: 'https://leetcode.com/problems/binary-tree-level-order-traversal/',
        },
      ],
    },
    {
      day: 1,
      type: 'article',
      title: 'Graph Representations: Adjacency List vs Matrix',
      description:
        'Short read on the memory and traversal-cost trade-offs. Know when a matrix is actually the right call.',
      links: [
        {
          label: 'Read article',
          url: 'https://en.wikipedia.org/wiki/Adjacency_list',
        },
      ],
    },
    {
      day: 2,
      type: 'system-design',
      title: 'Design a News Feed',
      description:
        'The fan-out problem: push vs. pull vs. hybrid. Work through celebrity users, feed ranking, and cache strategy. This is the most commonly asked design question — take it seriously.',
      links: [
        {
          label: 'Prompt & rubric',
          url: 'https://github.com/donnemartin/system-design-primer',
        },
      ],
    },
    {
      day: 3,
      type: 'leetcode',
      title: 'Number of Islands',
      difficulty: 'Medium',
      description:
        'Grid DFS/BFS flood fill. Practice both the recursive DFS and the iterative BFS version, and mention union-find as an alternative.',
      links: [
        {
          label: 'LeetCode #200',
          url: 'https://leetcode.com/problems/number-of-islands/',
        },
      ],
    },
    {
      day: 4,
      type: 'exercise',
      title: 'Build a Graph Traversal Library',
      description:
        'On-platform exercise: implement BFS, DFS (iterative + recursive), and topological sort against a common graph interface. Tests include cycle detection edge cases.',
      links: [],
    },
    {
      day: 5,
      type: 'leetcode',
      title: 'Course Schedule',
      difficulty: 'Medium',
      description:
        "Topological sort / cycle detection. Do it with Kahn's algorithm and with DFS coloring. Explain the difference out loud.",
      links: [
        {
          label: 'LeetCode #207',
          url: 'https://leetcode.com/problems/course-schedule/',
        },
      ],
    },
  ],
  // Week 3 (offset -1): Dynamic Programming I
  [
    {
      day: 0,
      type: 'leetcode',
      title: 'Climbing Stairs',
      difficulty: 'Easy',
      description:
        'The gateway DP problem. Solve top-down with memoization first, then convert to bottom-up, then optimize space to O(1). Narrate each transformation.',
      links: [
        {
          label: 'LeetCode #70',
          url: 'https://leetcode.com/problems/climbing-stairs/',
        },
      ],
    },
    {
      day: 0,
      type: 'video',
      title: 'DP for People Who Hate DP',
      description:
        'Reframes DP as "recursion + caching" instead of magic tables. The 5-step framework at 22:00 is the one we use in coaching sessions.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=dynamic+programming+explained',
        },
      ],
    },
    {
      day: 1,
      type: 'leetcode',
      title: 'House Robber',
      difficulty: 'Medium',
      description:
        'Classic take-or-skip decision. Write the recurrence relation on paper before coding anything.',
      links: [
        {
          label: 'LeetCode #198',
          url: 'https://leetcode.com/problems/house-robber/',
        },
      ],
    },
    {
      day: 1,
      type: 'article',
      title: 'Identifying DP Problems in the Wild',
      description:
        'The checklist: optimal substructure + overlapping subproblems. Includes 10 problem statements to classify as DP or not — do all 10.',
      links: [
        {
          label: 'Read article',
          url: 'https://leetcode.com/discuss/study-guide',
        },
      ],
    },
    {
      day: 2,
      type: 'system-design',
      title: 'Design a Chat System',
      description:
        'WebSockets vs. long polling, message ordering, delivery guarantees, and online presence. Draw the connection-server architecture and explain how you scale it.',
      links: [
        {
          label: 'Prompt & rubric',
          url: 'https://github.com/donnemartin/system-design-primer',
        },
      ],
    },
    {
      day: 3,
      type: 'leetcode',
      title: 'Coin Change',
      difficulty: 'Medium',
      description:
        'Unbounded knapsack pattern. A very common phone-screen question — make sure you can produce the bottom-up table version quickly.',
      links: [
        {
          label: 'LeetCode #322',
          url: 'https://leetcode.com/problems/coin-change/',
        },
      ],
    },
    {
      day: 4,
      type: 'exercise',
      title: 'DP Recurrence Writing Drill',
      description:
        'On-platform drill: for eight problem statements, write only the recurrence relation and base cases (no code). Graded on correctness of the state definition.',
      links: [],
    },
    {
      day: 5,
      type: 'leetcode',
      title: 'Longest Increasing Subsequence',
      difficulty: 'Medium',
      description:
        'Do the O(n²) DP first, then the O(n log n) patience-sorting version. Being able to explain the binary-search variant is a strong senior signal.',
      links: [
        {
          label: 'LeetCode #300',
          url: 'https://leetcode.com/problems/longest-increasing-subsequence/',
        },
      ],
    },
  ],
  // Week 4 (offset 0, current): System Design Foundations
  [
    {
      day: 0,
      type: 'video',
      title: 'Back-of-the-Envelope Estimation',
      description:
        'Latency numbers every engineer should know, plus QPS and storage estimation walk-throughs. Memorize the latency table — you will be quizzed on it Thursday.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=back+of+envelope+estimation+system+design',
        },
      ],
    },
    {
      day: 0,
      type: 'leetcode',
      title: 'Merge Intervals',
      difficulty: 'Medium',
      description:
        'Keeping algorithm skills warm during design week. Sort by start, then sweep. Watch the boundary conditions on touching intervals.',
      links: [
        {
          label: 'LeetCode #56',
          url: 'https://leetcode.com/problems/merge-intervals/',
        },
      ],
    },
    {
      day: 1,
      type: 'article',
      title: 'CAP Theorem Without the Hand-Waving',
      description:
        'What CAP actually says (and what it does not). Focus on the section distinguishing CP vs. AP system examples — you should be able to give two of each.',
      links: [
        {
          label: 'Read article',
          url: 'https://en.wikipedia.org/wiki/CAP_theorem',
        },
      ],
    },
    {
      day: 1,
      type: 'exercise',
      title: 'Capacity Estimation Worksheet',
      description:
        'On-platform worksheet: estimate storage, bandwidth, and QPS for three product scenarios. Answers within an order of magnitude count as correct.',
      links: [],
    },
    {
      day: 2,
      type: 'system-design',
      title: 'Design Instagram',
      description:
        'Full 45-minute mock format. Cover photo storage, feed generation, and the metadata data model. Time-box yourself: 5 min requirements, 5 min estimation, 15 min high-level, 15 min deep-dive, 5 min wrap.',
      links: [
        {
          label: 'Prompt & rubric',
          url: 'https://github.com/donnemartin/system-design-primer',
        },
      ],
    },
    {
      day: 3,
      type: 'video',
      title: 'Database Sharding Strategies',
      description:
        'Range vs. hash vs. directory-based sharding, plus resharding pain. Note the consistent-hashing segment — it connects to Thursday\u2019s exercise.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=database+sharding',
        },
      ],
    },
    {
      day: 3,
      type: 'leetcode',
      title: 'LRU Cache',
      difficulty: 'Medium',
      description:
        'Revisiting LRU two weeks after the from-scratch exercise — this time on LeetCode under time pressure. Target: under 25 minutes.',
      links: [
        {
          label: 'LeetCode #146',
          url: 'https://leetcode.com/problems/lru-cache/',
        },
      ],
    },
    {
      day: 4,
      type: 'exercise',
      title: 'Implement Consistent Hashing',
      description:
        'On-platform exercise: build a consistent-hash ring with virtual nodes. Tests verify key redistribution stays under 30% when a node is added.',
      links: [],
    },
    {
      day: 5,
      type: 'system-design',
      title: 'Design a Distributed Cache',
      description:
        'Ties the whole week together: sharding, consistent hashing, eviction policies, and cache invalidation strategies. Record yourself and self-review before Sunday.',
      links: [
        {
          label: 'Prompt & rubric',
          url: 'https://github.com/donnemartin/system-design-primer',
        },
      ],
    },
    {
      day: 3,
      type: 'meeting',
      title: 'Accountability call with Coach Maya',
      description:
        'Your weekly 1:1. This week: review the Instagram mock feedback, unblock the CAP theorem article questions, and lock in next sprint\u2019s focus. Completes automatically when you attend.',
      links: [{ label: 'Join meeting', url: '/interview/acc-thursday' }],
      meeting: {
        meetingId: 'acc-thursday',
        kind: 'accountability',
        time: '3:00 PM',
      },
    },
    {
      day: 4,
      type: 'meeting',
      title: 'Peer interview: Priya S.',
      description:
        'Peer-to-peer coding interview — you are the interviewer this round. The AI has picked a question suited to Priya\u2019s level; review it and the solution before the call. Completes automatically when you attend.',
      links: [{ label: 'Join meeting', url: '/interview/peer-friday' }],
      meeting: {
        meetingId: 'peer-friday',
        kind: 'peer',
        time: '8:00 PM',
        role: 'interviewer',
        peerName: 'Priya S.',
        peerLevel: 'intermediate',
        suggestedQuestion: {
          title: 'Edge List Conversion II',
          url: '/interview/peer-friday',
          solutionUrl:
            'https://leetcode.com/problems/course-schedule/solutions/',
        },
      },
    },
  ],
  // Week 5 (offset +1, locked): Heaps & Intervals
  [
    {
      day: 0,
      type: 'leetcode',
      title: 'Kth Largest Element in an Array',
      difficulty: 'Medium',
      description:
        'Heap vs. quickselect. Know the average and worst cases of both approaches cold.',
      links: [
        {
          label: 'LeetCode #215',
          url: 'https://leetcode.com/problems/kth-largest-element-in-an-array/',
        },
      ],
    },
    {
      day: 0,
      type: 'video',
      title: 'Heaps Demystified',
      description:
        'Array-backed heap mechanics: sift-up, sift-down, and heapify in O(n). Includes the classic "why is heapify linear" proof.',
      links: [
        {
          label: 'Watch on YouTube',
          url: 'https://www.youtube.com/results?search_query=binary+heap+explained',
        },
      ],
    },
    {
      day: 1,
      type: 'leetcode',
      title: 'Find Median from Data Stream',
      difficulty: 'Hard',
      description:
        'The two-heap technique. This pattern shows up in streaming and percentile questions constantly.',
      links: [
        {
          label: 'LeetCode #295',
          url: 'https://leetcode.com/problems/find-median-from-data-stream/',
        },
      ],
    },
    {
      day: 2,
      type: 'system-design',
      title: 'Design a Job Scheduler',
      description:
        'Delayed jobs, retries, priorities, and exactly-once vs. at-least-once execution. Connects heaps (priority queues) to real infrastructure.',
      links: [
        {
          label: 'Prompt & rubric',
          url: 'https://github.com/donnemartin/system-design-primer',
        },
      ],
    },
    {
      day: 3,
      type: 'leetcode',
      title: 'Meeting Rooms II',
      difficulty: 'Medium',
      description:
        'Min-heap of end times, or the chronological-ordering trick. A staple interval question.',
      links: [
        {
          label: 'LeetCode #253',
          url: 'https://leetcode.com/problems/meeting-rooms-ii/',
        },
      ],
    },
    {
      day: 4,
      type: 'exercise',
      title: 'Build a Priority Queue with Decrease-Key',
      description:
        'On-platform exercise: indexed binary heap supporting decrease-key in O(log n). Foundation for Dijkstra next sprint.',
      links: [],
    },
    {
      day: 5,
      type: 'article',
      title: 'Interval Problems: A Field Guide',
      description:
        'Taxonomy of interval questions: merging, scheduling, stabbing, and sweeping. Read before the Saturday problem set.',
      links: [
        {
          label: 'Read article',
          url: 'https://leetcode.com/discuss/study-guide',
        },
      ],
    },
  ],
]

// Coach comments keyed by [weekIndex, taskIndexInWeek]. read = starts as read.
const COACH_COMMENTS: Record<
  string,
  { comments: Omit<CoachComment, 'id'>[]; read: boolean }
> = {
  '0-2': {
    read: true,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Nice work choosing the character-count key here. One nitpick: you sorted inside the loop in your first attempt, which made it O(n · k log k). You caught it, but try to catch that before running the code next time.',
        aspect: 'complexity',
        date: '',
      },
    ],
  },
  '0-4': {
    read: true,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Good first design! Your capacity estimation was solid. Two gaps: you never discussed what happens when the key-generation service goes down, and your data model had no index on the short code. We will drill availability questions next session.',
        aspect: 'general',
        date: '',
      },
      {
        author: 'Coach Maya',
        text: 'Follow-up: re-watch your recording at 18:30 — you went silent for almost 2 minutes while thinking. Practice thinking out loud even when unsure.',
        aspect: 'duration',
        date: '',
      },
    ],
  },
  '1-2': {
    read: true,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Your 3Sum dedupe logic is clean now. Well done — this was rough two weeks ago. Keep the sorted-input assumption in your opening clarifying questions.',
        aspect: 'code',
        date: '',
      },
    ],
  },
  '1-7': {
    read: false,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Reviewed your Minimum Window Substring submission. The need/have counters are correct, but your variable naming (a, b, c, mp) would hurt you in a real interview. Readable code is part of the evaluation — resubmit with clear names when you get a chance.',
        aspect: 'code',
        date: '',
      },
    ],
  },
  '2-4': {
    read: true,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Strong news feed design. Your hybrid fan-out answer for celebrity users was exactly right. Next time, proactively bring up feed ranking before I ask — it shows product sense.',
        aspect: 'general',
        date: '',
      },
    ],
  },
  '2-6': {
    read: false,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Your topological sort had a subtle bug: you never handled disconnected components in the DFS version (the test suite caught it). Kahn\u2019s version was perfect though. Review the DFS coloring approach once more.',
        aspect: 'code',
        date: '',
      },
      {
        author: 'Coach Maya',
        text: 'Also — nice improvement on the cycle detection edge cases. 9/10 on this exercise overall.',
        aspect: 'post-mortem',
        date: '',
      },
    ],
  },
  '3-4': {
    read: false,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Chat system design review: good instinct going straight to WebSockets, and your message-ordering discussion (server timestamps + sequence numbers) was senior-level. Gap: you hand-waved presence. "Heartbeat + TTL in Redis" needs specifics — what TTL? What happens on flaky connections?',
        aspect: 'post-mortem',
        date: '',
      },
    ],
  },
  '3-5': {
    read: false,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Coin Change: correct, but you jumped to bottom-up without explaining the recurrence first. Interviewers want to see the thought process. Slow down — you have the time.',
        aspect: 'duration',
        date: '',
      },
    ],
  },
  '3-7': {
    read: true,
    comments: [
      {
        author: 'Coach Maya',
        text: 'You got the O(n log n) LIS! That puts you ahead of schedule on DP. We will do DP II (2-D problems) in sprint 16.',
        aspect: 'complexity',
        date: '',
      },
    ],
  },
  '4-4': {
    read: false,
    comments: [
      {
        author: 'Coach Maya',
        text: 'Instagram mock notes: your time-boxing was excellent — first time you finished the deep-dive with time to spare. Main feedback: your photo-storage answer defaulted to "S3" without discussing CDN strategy. Read the CAP article from Tuesday again with the photo-serving path in mind.',
        aspect: 'post-mortem',
        date: '',
      },
    ],
  },
}

// Days into the past week the comment was left (relative to that week's Sunday)
function commentDate(weekStart: Date, day: number): string {
  return addDays(weekStart, day + 1).toISOString()
}

export interface HomeworkData {
  tasks: HomeworkTask[]
  sprints: Sprint[]
}

let cached: HomeworkData | null = null

/**
 * Generates ~6 weeks of homework relative to the real current date:
 * 4 past weeks, the current week, and 1 locked future week.
 */
export function getHomeworkData(): HomeworkData {
  if (cached) return cached

  const thisMonday = currentWeekStart()
  const today = new Date()
  const todayKey = toKey(today)

  const tasks: HomeworkTask[] = []
  const sprints: Sprint[] = []

  WEEK_PLANS.forEach((plan, weekIndex) => {
    const offset = weekIndex - 4 // -4 .. +1
    const weekStart = addDays(thisMonday, offset * 7)
    const sprintNumber = CURRENT_SPRINT_NUMBER + offset

    sprints.push({
      number: sprintNumber,
      startDate: toKey(weekStart),
      endDate: toKey(addDays(weekStart, 6)),
      theme: WEEK_THEMES[weekIndex],
    })

    plan.forEach((tpl, taskIndex) => {
      const date = addDays(weekStart, tpl.day)
      const dateKey = toKey(date)
      const id = `t-${weekIndex}-${taskIndex}`

      let completed = false
      if (offset < 0) {
        const missed =
          (weekIndex === 1 && taskIndex === 6) ||
          (weekIndex === 3 && taskIndex === 6)
        completed = !missed
      } else if (offset === 0) {
        completed =
          dateKey < todayKey ||
          tpl.type === 'leetcode' ||
          tpl.type === 'exercise'
      }

      const commentEntry = COACH_COMMENTS[`${weekIndex}-${taskIndex}`]
      const comments: CoachComment[] = commentEntry
        ? commentEntry.comments.map((c, i) => ({
            ...c,
            id: `${id}-c${i}`,
            date: commentDate(weekStart, tpl.day),
          }))
        : []

      tasks.push({
        id,
        title: tpl.title,
        type: tpl.type,
        date: dateKey,
        description: tpl.description,
        difficulty: tpl.difficulty,
        links: tpl.links,
        platformTracked:
          tpl.type === 'leetcode' ||
          tpl.type === 'exercise' ||
          tpl.type === 'meeting',
        completed,
        comments,
        commentsRead: commentEntry ? commentEntry.read : true,
        meeting: tpl.meeting,
      })
    })
  })

  cached = { tasks, sprints }
  return cached
}
